import {
	InfractionDetails,
	IS_NOT_EMPTY_LIST,
	IS_NOT_NULL_OR_UNDEFINED,
	IS_NULL_OR_UNDEFINED,
} from 'cargo-chief-commons';

export const GET_QUALITY_CLASS = (
	quality: string,
	carrierActive: boolean,
	forBrokerQualityData: boolean = false
) => {
	if (!quality) {
		if (!carrierActive && !forBrokerQualityData) {
			return 'Unacceptable';
		}
		return '';
	}

	// Convert to lowercase for consistent comparison
	const qualityLower = quality.toLowerCase().trim();

	// Green - Pass/Acceptable
	if (['pass', 'acceptable', 'certified'].includes(qualityLower)) {
		return 'Pass';
	}

	// Red - Fail/Unacceptable
	if (['fail', 'unacceptable', 'not certified'].includes(qualityLower)) {
		return 'Fail';
	}

	// Yellow - Partial Pass/Moderate
	if (['partial_pass', 'partial pass', 'moderate'].includes(qualityLower)) {
		return 'Moderate';
	}

	// Default - no color class for unknown/other values
	return '';
};

export const GET_QUALITY_DISPLAY = (
	quality: string,
	carrierActive: boolean,
	forBrokerQualityData: boolean = false,
	isMCPEnabled: boolean = false
) => {
	if (!quality && forBrokerQualityData) {
		return '-';
	}
	if (quality) {
		const qualityLower = quality.toLowerCase().trim();
		// Special case: Display "Unacceptable Review" for MCP
		if (isMCPEnabled && qualityLower === 'unacceptable') {
			return 'Unacceptable Review';
		}
		// Convert to lowercase first, then replace underscores with spaces, then convert to title case
		return quality
			.toLowerCase()
			.replace(/_/g, ' ')
			.replace(/\b\w/g, (char) => char.toUpperCase());
	} else if (!carrierActive && !forBrokerQualityData) {
		return 'Unacceptable';
	}
	return 'N/A';
};

export const GET_CC_OVERALL_CLASS = (
	score: string,
	carrierActive: boolean,
	forBrokerQualityData: boolean = false
) => {
	if (score && score !== 'N/A') {
		return score;
	} else if (!forBrokerQualityData && !carrierActive) {
		return 'Low';
	}
	return 'N/A';
};

export const GET_CC_OVERALL_DISPLAY = (
	score: string,
	carrierActive: boolean,
	forBrokerQualityData: boolean = false
) => {
	if (score && score !== 'N/A') {
		return score;
	} else if (!forBrokerQualityData && !carrierActive) {
		return 'Low';
	} else if (forBrokerQualityData) {
		return '';
	}
	return 'N/A';
};

/**
 * Resolve the effective carrier quality score for a NETWORK carrier the same way the project
 * quality filter does: prefer the account-scoped ACM cc_score, falling back to the carrier-level
 * cc_score when the account's ACM cc_score is null. Returns null for an out-of-network carrier
 * (no accountCarrierMaps) — the project quality filter excludes those, so the display must not
 * invent a carrier-level band the filter would never match — and null when no score is set, so
 * callers render "N/A". Keeps the quality display and the quality filter in agreement. CN-8744.
 */
export const RESOLVE_EFFECTIVE_CC_SCORE = (carrier: any, accountId: number): number => {
	if (IS_NULL_OR_UNDEFINED(carrier)) {
		return null;
	}

	const accountCarrierMaps = carrier.accountCarrierMaps;
	// Out-of-network (no account maps) → no band, matching the filter's network gate.
	if (!accountCarrierMaps || accountCarrierMaps.length === 0) {
		return null;
	}

	const accountCarrierMap = accountCarrierMaps.find(
		(acm) => IS_NOT_NULL_OR_UNDEFINED(acm) && acm.accountId === accountId
	);

	if (IS_NOT_NULL_OR_UNDEFINED(accountCarrierMap?.ccScore)) {
		return accountCarrierMap.ccScore;
	}

	return IS_NOT_NULL_OR_UNDEFINED(carrier.ccScore) ? carrier.ccScore : null;
};

export const COMBINE_ALL_INFRACTIONS = (infractionDetails: InfractionDetails): string[] => {
	const allInfractions = [];
	if (IS_NOT_NULL_OR_UNDEFINED(infractionDetails)) {
		if (IS_NOT_NULL_OR_UNDEFINED(infractionDetails.authority)) {
			allInfractions.push(...infractionDetails.authority);
		}
		if (IS_NOT_NULL_OR_UNDEFINED(infractionDetails.insurance)) {
			allInfractions.push(...infractionDetails.insurance);
		}
		if (IS_NOT_NULL_OR_UNDEFINED(infractionDetails.operations)) {
			allInfractions.push(...infractionDetails.operations);
		}
		if (IS_NOT_NULL_OR_UNDEFINED(infractionDetails.safety)) {
			allInfractions.push(...infractionDetails.safety);
		}
		if (IS_NOT_NULL_OR_UNDEFINED(infractionDetails.other)) {
			allInfractions.push(...infractionDetails.other);
		}
		if (IS_NOT_NULL_OR_UNDEFINED(infractionDetails.overall)) {
			allInfractions.push(...infractionDetails.overall);
		}
	}
	return allInfractions;
};

export const TOOL_TIP_MESSAGE = (infractions: string[]): string => {
	if (IS_NOT_EMPTY_LIST(infractions)) {
		return infractions.join('\n');
	}
};
